import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTinodeConnection } from "../../../hooks/useTinodeConnection";
import { useTinodeMessages } from "../../../hooks/useTinodeMessages";
import { subscribeToTopicWithMessages } from "../../../services/tinodeApi";
import {
  isSessionEndMessage,
  isSessionEndedFromMessages,
} from "../../../services/tinodeSessionService";
import { parseTinodeError } from "../../../utils/tinodeErrorHelper";
import TinodeLoadingScreen from "./tinodeComponent/TinodeLoadingScreen";
import ChatView from "./tinodeComponent/ChatView";
import { IoArrowBack } from "react-icons/io5";
import { RiChatDeleteLine } from "react-icons/ri";
import { FaRegUser } from "react-icons/fa";
import { endConsultationSession } from "../../../services/tinodeSessionService";
import SwipeConfirm from "../../../components/swipeComp/SwipeConfirm";
import { parseTopicFn } from "../../../services/tinodeConsultationGroupService";
import SessionCountdownBanner from "./tinodeComponent/SessionCountdownBanner";
import { getConsultationSessionStatus } from "../../../services/telemedicineApi";
import FullsizeButton from "../../../components/common/FullsizeButton";
import { GlobalStateContext } from "../../../components/context/GlobalStateContext";

const SESSION_KEY = "tinode_chat_session";
const SESSION_DURATION_MS = 30 * 60 * 1000;

const saveSession = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch (_) {}
};
const loadSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const clearSession = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (_) {}
};

const waitUntilAuthenticated = (tinode, timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    if (tinode.isAuthenticated()) {
      resolve();
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      if (tinode.isAuthenticated()) {
        clearInterval(interval);
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        clearInterval(interval);
        reject(new Error("Authentication timeout"));
      }
    }, 200);
  });
};

const TinodeChatCustomer = () => {
  const { setTopicId, setCustomerChatUnreadCount } =
    useContext(GlobalStateContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { topic } = useParams();
  const { type } = location.state ?? {};

  // Session data dari navigation state atau sessionStorage
  const sessionData = useMemo(() => {
    const fromState = location.state;
    if (fromState?.topicName) {
      saveSession({
        topicName: fromState.topicName,
        groupName: fromState.groupName,
        doctorId: fromState.doctorId,
        invitedUsers: fromState.invitedUsers,
        consultationData: fromState.consultationData,
      });
      return fromState;
    }
    return loadSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { topicName, groupName, doctorId, invitedUsers, consultationData } =
    sessionData || {};

  // console.log("INI TOPIC NAME DARI USER CHAT 123", sessionData);
  // console.log("INI GROUP NAME DARI USER CHAT", groupName);
  // console.log("INI DOCTOR ID DARI USER CHAT", doctorId);
  // console.log("INI INVITED USERS DARI USER CHAT", invitedUsers);
  // console.log("INI CONSUL DATA DARI USER CHAT", consultationData);

  const user = useMemo(
    () => ({
      fullname: localStorage.getItem("fullname") || "User",
      email: localStorage.getItem("email") || "",
      id: localStorage.getItem("user_id") || "",
    }),
    [],
  );

  // topicName tidak berubah dalam session ini, tidak perlu state dengan setter
  const selectedTopic = topic ? topic : topicName;
  // const selectedTopic = "grpkmoBmEaUlaw";

  const [isLoadingTopic, setIsLoadingTopic] = useState(!!topicName);
  const [topicLoadError, setTopicLoadError] = useState(null);
  const [isSessionOver, setIsSessionOver] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [sessionWindowFromAPI, setSessionWindowFromAPI] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());

  const fileInputRef = useRef(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setCustomerChatUnreadCount(0);
  }, [setCustomerChatUnreadCount]);

  const {
    tinodeRef,
    isReady,
    isConnected,
    isLoading,
    error,
    topics,
    setTopics,
    userInfo,
  } = useTinodeConnection(user);

  // const selectedChat = Array.isArray(userInfo)
  //   ? userInfo.find((item) => item.id === topic)
  //   : null;
  const selectedChat = useMemo(() => {
    if (!topics) return null;
    return topics?.find(
      (item) => item.id === selectedTopic || item.topic === selectedTopic,
    );
  }, [topics, selectedTopic]);

  const meta = parseTopicFn(selectedChat && selectedChat.public?.fn);

  const doctorTinodeUID = useMemo(() => {
    const doctorIdValue =
      typeof doctorId === "string" ? doctorId.trim() : String(doctorId || "");
    if (doctorIdValue.startsWith("usr")) {
      return doctorIdValue;
    }

    if (!Array.isArray(invitedUsers)) return "";
    const firstDoctorUID = invitedUsers.find(
      (uid) => typeof uid === "string" && uid.startsWith("usr"),
    );
    return firstDoctorUID || "";
  }, [doctorId, invitedUsers]);

  /**
   * FIX: useTinodeMessages adalah satu-satunya instance untuk halaman ini.
   * ChatView menerima semua data ini sebagai props — tidak ada hook kedua.
   */
  const handleMessageReceived = useCallback((msg) => {
    if (isSessionEndMessage(msg)) {
      setIsSessionOver(true);
    }
  }, []);

  const {
    messages,
    setMessages,
    messagesEndRef,
    messagesContainerRef,
    messageSeqSetRef,
    optimisticMessagesRef,
    pendingRealSeqRef,
    loadTopicMessages,
    sendOptimisticMessage,
    removeOptimisticMessage,
    loadOlderMessages,
    mapOptimisticToRealSeq, // FIX: Helper untuk mapping optId → realSeq
  } = useTinodeMessages(tinodeRef, selectedTopic, handleMessageReceived);

  const syncSessionStatus = useCallback(async () => {
    if (!selectedTopic) return null;

    try {
      const status = await getConsultationSessionStatus({
        topic_name: selectedTopic,
      });

      if (status?.started_at || status?.expires_at) {
        setSessionWindowFromAPI({
          startAt: status?.started_at || null,
          expiresAt: status?.expires_at || null,
        });
      }

      if (
        status?.status === 5 ||
        status?.status === 6 ||
        status?.can_chat === false ||
        (status?.is_expired && status?.status === 4)
      ) {
        setIsSessionOver(true);
      }

      return status;
    } catch (error) {
      console.warn("Failed to sync consultation session status:", error);
      return null;
    }
  }, [selectedTopic]);

  const explicitSessionWindow = useMemo(() => {
    const startedAt =
      consultationData?.startedAt || consultationData?.started_at || null;
    const expiresAt =
      consultationData?.expiresAt || consultationData?.expires_at || null;

    if (!startedAt && !expiresAt) return null;

    return { startAt: startedAt, expiresAt };
  }, [consultationData]);

  const inferredSessionWindow = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) {
      return null;
    }

    const currentUserUID = tinodeRef.current?.getCurrentUserID?.() || "";
    let firstDoctorMessageMs = null;

    for (const msg of messages) {
      const senderUID =
        typeof msg?.from === "string"
          ? msg.from.trim()
          : String(msg?.from || "");

      const isDoctorMessage = doctorTinodeUID
        ? senderUID === doctorTinodeUID
        : currentUserUID
          ? senderUID !== "" && senderUID !== currentUserUID
          : senderUID !== "";

      if (!isDoctorMessage) continue;

      const ts =
        typeof msg?.timestamp === "number"
          ? msg.timestamp
          : Date.parse(msg?.ts || msg?.timestamp || "");
      if (Number.isNaN(ts)) continue;
      if (firstDoctorMessageMs === null || ts < firstDoctorMessageMs) {
        firstDoctorMessageMs = ts;
      }
    }

    if (firstDoctorMessageMs === null) return null;

    return {
      startAt: new Date(firstDoctorMessageMs).toISOString(),
      expiresAt: new Date(
        firstDoctorMessageMs + SESSION_DURATION_MS,
      ).toISOString(),
    };
  }, [doctorTinodeUID, messages, tinodeRef]);

  const currentSessionWindow =
    sessionWindowFromAPI || explicitSessionWindow || inferredSessionWindow;

  const isSessionExpiredByTime = useMemo(() => {
    const expiresAtMs = Date.parse(currentSessionWindow?.expiresAt || "");
    if (Number.isNaN(expiresAtMs)) return false;
    return nowMs >= expiresAtMs;
  }, [currentSessionWindow, nowMs]);

  const isSessionLocked = isSessionOver || isSessionExpiredByTime;

  useEffect(() => {
    if (!selectedTopic) return;
    void syncSessionStatus();
  }, [selectedTopic, syncSessionStatus]);

  useEffect(() => {
    if (!isSessionExpiredByTime) return;
    if (isSessionOver) return;

    setIsSessionOver(true);
    void syncSessionStatus();
  }, [isSessionExpiredByTime, isSessionOver, syncSessionStatus]);

  useEffect(() => {
    if (!selectedTopic || !tinodeRef.current || !isReady) return;

    let isActive = true;

    const setupTopic = async (attempt = 0) => {
      if (!isActive) return;

      try {
        setTopicLoadError(null);
        if (attempt === 0) setIsLoadingTopic(true);

        await waitUntilAuthenticated(tinodeRef.current, 15000);
        if (!isActive) return;

        let topic = tinodeRef.current.getTopic(selectedTopic);
        if (!topic || !topic.isSubscribed()) {
          topic = await subscribeToTopicWithMessages(
            tinodeRef.current,
            selectedTopic,
          );
        }

        if (!isActive) return;

        // Cek apakah sesi sudah berakhir dari history
        if (isSessionEndedFromMessages(topic)) {
          setIsSessionOver(true);
        }

        // Load messages via hook (loadTopicMessages akan set messages state)
        await loadTopicMessages(selectedTopic);

        if (!isActive) return;

        // Mark as read
        if (topic.seq > 0) {
          setTimeout(() => {
            try {
              topic?.noteRead?.(topic.seq);
            } catch (_) {}
          }, 500);
        }

        setIsLoadingTopic(false);
      } catch (err) {
        if (!isActive) return;
        const errMsg = parseTinodeError(err);
        const isTransient =
          err instanceof Event ||
          err?.isTrusted !== undefined ||
          errMsg?.includes("timeout") ||
          errMsg?.includes("WebSocket") ||
          errMsg?.includes("401") ||
          errMsg?.includes("authentication") ||
          errMsg?.includes("503") ||
          errMsg?.includes("Websocket is not connected");

        if (isTransient && attempt < 5) {
          const delay = [2000, 3000, 4000, 5000, 6000][attempt] || 6000;
          setTimeout(() => {
            if (isActive) setupTopic(attempt + 1);
          }, delay);
        } else {
          setTopicLoadError(
            "Gagal memuat percakapan. Silakan coba refresh halaman.",
          );
          setIsLoadingTopic(false);
        }
      }
    };

    setupTopic();

    return () => {
      isActive = false;
    };
  }, [selectedTopic, isReady, loadTopicMessages]);

  console.log("INI currentSessionWindow", currentSessionWindow);
  console.log("INI isSessionLocked", isSessionLocked);
  console.log("INI TOPIC", topic);

  useEffect(() => {
    const handleTinodeReconnected = async () => {
      if (!tinodeRef.current || !tinodeRef.current.isAuthenticated()) {
        return;
      }

      if (!selectedTopic) {
        return;
      }

      console.log("🔄 Tinode reconnected, refreshing messages...");

      try {
        // Reload messages untuk selected topic
        await loadTopicMessages(selectedTopic);
      } catch (err) {
        console.warn("Failed to refresh messages after reconnect:", err);
      }
    };

    window.addEventListener("tinode-reconnected", handleTinodeReconnected);

    return () => {
      window.removeEventListener("tinode-reconnected", handleTinodeReconnected);
    };
  }, [selectedTopic, loadTopicMessages]);

  // FIX: Handle online/offline events untuk refresh setelah reconnect
  useEffect(() => {
    const handleOnline = async () => {
      if (!tinodeRef.current) return;

      // Wait a bit for connection to stabilize
      await new Promise((r) => setTimeout(r, 1000));

      if (
        tinodeRef.current.isConnected() &&
        tinodeRef.current.isAuthenticated()
      ) {
        console.log("🌐 Network online, refreshing messages...");
        try {
          if (selectedTopic) {
            await loadTopicMessages(selectedTopic);
          }
        } catch (err) {
          console.warn("Failed to refresh after online:", err);
        }
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [selectedTopic, loadTopicMessages]);

  useEffect(() => {
    if (!isSessionOver) return;

    // Selalu clear topicId dan session saat sesi berakhir (dari customer atau dokter),
    // agar bubble hilang dan user bisa chat baru lagi.
    setTopicId("");
    clearSession();

    if (type === 1) {
      const timer = setTimeout(() => {
        navigate("/consultation/history", {
          state: {
            topicName: selectedTopic,
            groupName,
            doctorId,
            consultationData,
            endedAt: new Date().toISOString(),
          },
        });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [
    isSessionOver,
    type,
    setTopicId,
    navigate,
    selectedTopic,
    groupName,
    doctorId,
    consultationData,
  ]);

  const handleEndSession = async () => {
    if (
      !selectedTopic ||
      !tinodeRef.current ||
      isEndingSession ||
      isSessionLocked
    )
      return;
    // const ok = window.confirm(
    //   "Apakah Anda yakin ingin mengakhiri sesi konsultasi ini?",
    // );
    // if (!ok) return;

    setIsEndingSession(true);
    try {
      await endConsultationSession(
        tinodeRef.current,
        selectedTopic,
        "customer",
      );
      setIsSessionOver(true);
    } catch (err) {
      alert("Gagal mengakhiri sesi: " + (parseTinodeError(err) || String(err)));
    } finally {
      setOpenConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <TinodeLoadingScreen
        message="Menghubungkan ke server chat..."
        subMessage={groupName ? `Konsultasi: ${groupName}` : "Mohon tunggu..."}
      />
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm max-w-md w-full mx-4">
          <div className="text-5xl mb-4">📡</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Koneksi Terputus
          </h2>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
            >
              🔄 Coba Lagi
            </button>
            {topicName && (
              <p className="text-xs text-gray-400">
                Sesi konsultasi Anda masih tersimpan.
              </p>
            )}
            <button
              onClick={() => {
                setTopicId("");
                clearSession();
                navigate("/tinode-consultation-group");
              }}
              className="px-6 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
            >
              Kembali ke Halaman Awal
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingTopic) {
    return (
      <TinodeLoadingScreen
        message="Memuat percakapan..."
        subMessage={groupName || "Mengambil riwayat chat..."}
      />
    );
  }

  if (topicLoadError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm max-w-md w-full mx-4">
          <div className="text-5xl mb-4">💬</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Gagal Memuat Chat
          </h2>
          <p className="text-gray-500 text-sm mb-6">{topicLoadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
          >
            🔄 Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTopic) {
    return (
      <div className="flex h-screen bg-gray-100">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">💬</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Tidak Ada Konsultasi
            </h2>
            <p className="text-gray-500 mb-4">
              Silakan buat group konsultasi terlebih dahulu
            </p>
            <button
              onClick={() => navigate("/tinode-consultation-group")}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Buat Konsultasi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 w-full">
        <div className="bg-white border-b border-gray-200 px-3 h-14 min-h-[56px] flex items-center gap-2 flex-shrink-0 relative">
          <button
            onClick={() => navigate("/")}
            className="p-1 rounded-full hover:bg-gray-100"
            type="button"
            aria-label="Kembali ke beranda"
          >
            <IoArrowBack size={20} className="text-gray-600" />
          </button>
          <div className="w-10 h-10 rounded-full overflow-hidden">
            {meta.doctorProfile === "" ? (
              <div className="bg-secondary w-full h-full flex items-center justify-center text-white">
                <FaRegUser size={20} />
              </div>
            ) : (
              <img
                src={meta.doctorProfile}
                alt=""
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-1">
              <h1 className="text-base font-semibold text-slate-700 line-clamp-1 leading-tight">
                {meta.doctorName}
              </h1>

              <div className="text-sm text-slate-500 flex items-center gap-2 min-w-0">
                <p>{meta.doctorSpecialty}</p>

                <p className="truncate min-w-0 max-w-[120px]">
                  SIP {meta.doctorSip}
                </p>
              </div>
            </div>
          </div>
          {!isSessionLocked && (
            <button
              onClick={() => setOpenConfirm(true)}
              disabled={isEndingSession || isSessionLocked}
              className="p-2 text-red-500 rounded-full hover:bg-red-200 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              title="Akhiri sesi konsultasi"
            >
              <RiChatDeleteLine size={20} />
            </button>
          )}

          {currentSessionWindow && !isSessionLocked ? (
            <div className="absolute -bottom-8 z-[9999] left-1/2 -translate-x-1/2">
              <SessionCountdownBanner
                startAt={currentSessionWindow.startAt}
                expiresAt={currentSessionWindow.expiresAt}
                isSessionOver={isSessionLocked}
                isCustomer={true}
              />
            </div>
          ) : null}

          {isSessionLocked && type === 1 && (
            <div className="bg-red-50 border-b border-red-200 px-4 py-3 text-center flex-shrink-0 absolute -bottom-14 left-0 right-0 z-[9999]">
              <p className="text-red-600 font-medium text-sm">
                Sesi konsultasi telah berakhir.
              </p>
              <p className="text-red-400 text-xs mt-1 animate-pulse">
                Anda akan dialihkan ke halaman histori konsultasi...
              </p>
            </div>
          )}

          {!isConnected && !isSessionLocked && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center flex-shrink-0 absolute -bottom-14 left-0 right-0 z-[9999]">
              <p className="text-yellow-700 text-xs">
                ⚡ Menghubungkan ulang... Pesan Anda tidak akan hilang.
              </p>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
          <ChatView
            selectedTopic={selectedTopic}
            tinodeRef={tinodeRef}
            messages={messages}
            messagesEndRef={messagesEndRef}
            messagesContainerRef={messagesContainerRef}
            messageSeqSetRef={messageSeqSetRef}
            loadOlderMessages={loadOlderMessages}
            setModalImageSrc={setModalImageSrc}
            setShowImageModal={setShowImageModal}
            isSessionOver={isSessionLocked}
            fileInputRef={fileInputRef}
            userInfo={user}
            fullWidth={true}
            sessionWindow={currentSessionWindow}
          />
          <FullsizeButton isAuth={false} />
        </div>
      </div>

      {showImageModal && modalImageSrc && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-5"
          onClick={() => setShowImageModal(false)}
        >
          <img
            src={modalImageSrc}
            alt="Preview"
            className="max-w-[90%] max-h-[90%] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-5 right-5 bg-white/20 text-white border-none rounded-full w-10 h-10 text-2xl cursor-pointer flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}

      <SwipeConfirm
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={"Akhiri Konsultasi?"}
        subTitle={"Akhiri"}
        desc={
          "Jika kamu keluar dari halaman ini, berarti kamu akan mengakhiri chat konsultasi."
        }
        handleConfirm={handleEndSession}
      />
    </div>
  );
};

export default TinodeChatCustomer;
